import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

function isValidServerActionEncryptionKey(value: string): boolean {
  try {
    const normalized = value.replace(/\s+/g, "");
    const decoded =
      typeof globalThis.atob === "function"
        ? globalThis.atob(normalized)
        : null;

    return decoded !== null && [16, 24, 32].includes(decoded.length);
  } catch {
    return false;
  }
}

/**
 * AES-256-GCM 키 검증 — base64 디코드 시 정확히 32바이트여야 한다.
 * `secret-cipher.ts`(server-core)가 양방향 자격증명 암호화에 사용.
 */
function isValidSecretEncryptionKey(value: string): boolean {
  try {
    const normalized = value.replace(/\s+/g, "");
    const decoded =
      typeof globalThis.atob === "function"
        ? globalThis.atob(normalized)
        : null;

    return decoded !== null && decoded.length === 32;
  } catch {
    return false;
  }
}

function formatValidationError(
  issues: readonly { message: string; path?: readonly (string | number)[] }[],
): never {
  const lines = issues.map((issue) => {
    const name = issue.path?.join(".") ?? "(unknown)";
    return `  - ${name}: ${issue.message}`;
  });

  console.error(
    [
      "",
      "❌ 환경변수 검증 실패",
      "",
      ...lines,
      "",
      "💡 .env 파일을 확인하거나, SKIP_ENV_VALIDATION=true로 검증을 건너뛸 수 있습니다.",
      "",
    ].join("\n"),
  );

  throw new Error("환경변수 검증 실패");
}

function handleInvalidAccess(variable: string): never {
  throw new Error(
    `서버 전용 환경변수 "${variable}"를 클라이언트에서 접근할 수 없습니다. clientEnv를 사용하세요.`,
  );
}

/**
 * 환경변수 타입 검증
 *
 * T3 Env를 사용하여 빌드 시점과 런타임에 환경변수를 검증합니다.
 * - server: 서버에서만 사용 가능한 환경변수 (시크릿)
 * - client: 클라이언트에서도 사용 가능한 환경변수 (NEXT_PUBLIC_* 접두사)
 *
 * 검증 건너뛰기:
 * - SKIP_ENV_VALIDATION=true 환경변수로 검증을 건너뛸 수 있습니다 (Docker 빌드 등)
 */
export const env = createEnv({
  /**
   * 서버 측 환경변수 스키마
   * 클라이언트 번들에 포함되지 않습니다.
   */
  server: {
    // === 필수 환경변수 ===
    NODE_ENV: z
      .enum(["development", "test", "production"])
      .default("development"),
    // 🔑 Aurora(`DATABASE_DRIVER=aurora`)에서는 URL이 없다 — 자격이 15분 IAM 토큰이라 URL로
    // 표현되지 않고 런타임(packages/db)은 AURORA_* 만 읽는다. 그래서 여기서는 optional이고,
    // "aurora가 아니면 필수"는 아래 createEnv 직후의 교차 규칙이 같은 에러 포맷으로 강제한다.
    // 2026-08-25 staging에서 옛 Prisma Postgres DATABASE_URL이 제거되자 이 줄의 min(1)이
    // 빌드를 죽였다 — 런타임은 그 값을 쓰지도 않는데.
    DATABASE_URL: z.string().optional(),
    AUTH_SECRET: z
      .string()
      .min(32, "AUTH_SECRET must be at least 32 characters"),
    BETTER_AUTH_SECRETS: z.string().optional(),

    // === 인증 (OAuth) ===
    // id.reopt.ai의 Google 소셜 로그인 자격.
    GOOGLE_CLIENT_ID: z.string().optional(),
    GOOGLE_CLIENT_SECRET: z.string().optional(),
    // Sign in with Apple (App Store 심사 4.8 — Google 로그인 제공 앱의 필수
    // 요건). 미설정 시 apple provider 자체가 등록되지 않는다 (조건부 spread).
    APPLE_CLIENT_ID: z.string().optional(), // Services ID (웹/네이티브 공용, 예: ai.reopt.signin)
    // ⚠️ ES256 서명 JWT — 최대 6개월 유효. 만료 전 재발급해 env 교체하는 운영
    // 주기가 필요하다 (Apple Developer 콘솔 키로 서명).
    APPLE_CLIENT_SECRET: z.string().optional(),
    APPLE_TEAM_ID: z.string().optional(),
    APPLE_KEY_ID: z.string().optional(),
    APPLE_PRIVATE_KEY: z.string().optional(),
    // 네이티브 시트 로그인(idToken 검증)의 aud 매칭용 iOS 번들 ID (ai.reopt.studio).
    APPLE_APP_BUNDLE_IDENTIFIER: z.string().optional(),

    // === MCP OAuth client 자격 ===
    // 워크스페이스 MCP 서버(GitHub) 연동용 OAuth App 자격. 로그인용 GOOGLE_*과
    // 별개의 client다 — 표준 GitHub OAuth App(콘솔 등록 필요).
    // 미설정 시 GitHub MCP 연동만 비활성(`isMcpOAuthConfigured`=false), 다른 기능엔
    // 영향 없음. 해석: packages/ai/lib/mcp-oauth-config.ts.
    GITHUB_MCP_CLIENT_ID: z.string().optional(),
    GITHUB_MCP_CLIENT_SECRET: z.string().optional(),
    // Gmail MCP용 Google Cloud OAuth client 자격. ⚠️ 로그인용 GOOGLE_CLIENT_ID와
    // **분리**(scope bleed 방지) — Gmail MCP는 gmail.readonly/compose 스코프를
    // 위임받으므로 로그인 client와 자격을 섞지 않는다. 표준 Google OAuth 2.0
    // (콘솔 등록 필요, DCR 아님). 미설정 시 Gmail MCP 연동만 비활성.
    GOOGLE_MCP_CLIENT_ID: z.string().optional(),
    GOOGLE_MCP_CLIENT_SECRET: z.string().optional(),

    // === 데이터베이스 ===
    // direct URL 별칭 — 이름은 옛 Vercel × Prisma 통합에서 왔지만 지금은 로컬 워크트리
    // 도구(scripts/worktree-db.sh)와 aurora-exec가 같은 이름으로 채운다.
    // 마이그레이션·시드에서 우선 사용하고, 런타임은 DATABASE_URL을 사용한다.
    DATABASE_PRISMA_DATABASE_URL: z.string().optional(),
    DATABASE_POSTGRES_URL: z.string().optional(),
    // Legacy — 로컬 .env / 일부 배포에서 여전히 사용. fallback으로 유지.
    PRISMA_DIRECT_URL: z.string().optional(),

    // === 데이터베이스 드라이버 전환 (Vercel × AWS Aurora POC) ===
    // "aurora"일 때만 packages/db가 IAM 인증 경로로 붙는다. 변수를 지우면 URL 경로
    // (로컬 DB)로 돌아간다. 옛 Prisma Postgres 롤백 자산은 2026-09-05에 회수했다.
    // ⚠️ AURORA_* 변수의 **존재**만으로는 전환되지 않는다(마켓플레이스 통합이 Preview
    //    환경 전체에 그 변수들을 주입하기 때문). 상세: packages/db/CLAUDE.md
    DATABASE_DRIVER: z.enum(["aurora"]).optional(),
    // Vercel × AWS 마켓플레이스가 주입 (Sensitive — pull하면 빈 문자열).
    // 접두사 없는 PGHOST/PGUSER/… 로 들어오면 packages/db가 폴백으로 읽는다.
    AURORA_PGHOST: z.string().optional(),
    AURORA_PGPORT: z.string().optional(),
    AURORA_PGUSER: z.string().optional(),
    AURORA_PGDATABASE: z.string().optional(),
    AURORA_PGSSLMODE: z.string().optional(),
    AURORA_AWS_REGION: z.string().optional(),
    AURORA_AWS_ROLE_ARN: z.string().optional(),
    // AWS RDS 글로벌 CA 번들(PEM). 있으면 **런타임** 서버 인증서를 검증한다.
    AURORA_SSL_CA: z.string().optional(),
    // 🔴 진단 전용 — 손으로 발급한 15분짜리 IAM auth token. 배포 환경에 넣지 말 것
    //    (만료 후 새 커넥션이 전부 인증 실패한다). 상세: packages/db/CLAUDE.md
    AURORA_AUTH_TOKEN: z.string().optional(),

    // === 캐시 (Redis) ===
    REDIS_URL: z.string().optional(),

    // === 분석 데이터 플레인 (reopt-data, RFC-0023) ===
    // URL은 배포 환경에서 자동 파생(prod data.reopt.ai / staging data.reopt.io —
    // server-core/lib/reopt-data-url.ts). REOPT_DATA_API_URL은 로컬 dev override 전용.
    // 기능 스위치는 비밀이다: CLIENT_ID/SECRET(운영 텔레메트리, reopt-ops Project 서버
    // 자격) · REOPT_PLATFORM_KEY(프로비저닝 — reopt-data와 같은 이름·같은 값).
    REOPT_DATA_CLIENT_ID: z.string().optional(),
    REOPT_DATA_CLIENT_SECRET: z.string().optional(),
    REOPT_DATA_API_URL: z.string().url().optional(),
    // LLM 관측(`$ai_*`)을 **별도 Project**로 보낼 때만 채운다 — 크레딧 귀속과 본문
    // 수집 설정을 운영 텔레메트리와 분리하려는 경우. 비어 있으면 위 자격을 그대로 쓴다.
    REOPT_DATA_AI_CLIENT_ID: z.string().optional(),
    REOPT_DATA_AI_CLIENT_SECRET: z.string().optional(),
    REOPT_PLATFORM_KEY: z.string().optional(),

    // === 결제 ===
    PADDLE_API_KEY: z.string().optional(),
    PADDLE_NOTIFICATION_WEBHOOK_SECRET: z.string().optional(),
    TOSS_SECRET_KEY: z.string().optional(),
    TOSS_BILLING_SECRET_KEY: z.string().optional(),
    TOSS_WEBHOOK_SECRET: z.string().optional(),

    // === 이메일 ===
    EMAIL_MAILER: z.enum(["resend", "nodemailer"]).optional(),
    EMAIL_SENDER: z.string().email().optional(),
    EMAIL_FEEDBACK: z.string().email().optional(),
    /// Vercel Resend 통합이 주입 (Sensitive) — 이름 고정, 임의 개명 금지
    RESEND_API_KEY: z.string().optional(),
    /// Vercel Resend 통합이 주입하는 발신 도메인(예: reopt.ai).
    /// 발신 주소는 EMAIL_SENDER가 권위이고, 미설정 시에만 noreply@<domain>으로 파생한다.
    RESEND_EMAIL_DOMAIN: z.string().optional(),
    /// Resend webhook(svix) 서명 검증 시크릿 — bounce/complaint 수신 엔드포인트
    EMAIL_RESEND_WEBHOOK_SECRET: z.string().optional(),
    EMAIL_SERVER: z.string().optional(),
    EMAIL_SERVER_HOST: z.string().optional(),
    EMAIL_SERVER_PORT: z.string().optional(),
    EMAIL_SERVER_USER: z.string().optional(),
    EMAIL_SERVER_PASS: z.string().optional(),

    // === 모바일 OS 푸시 (RFC-0010) ===
    // Expo Push API access token. 미설정 시 푸시 발송이 no-op으로 스킵된다
    // (코드 선배포 안전 — send-expo-push.ts).
    EXPO_ACCESS_TOKEN: z.string().optional(),

    // === 멤버(스튜디오 셸 앱) 직접 FCM/APNs 푸시 (통합 푸시 서비스) ===
    // 플랫폼 소유 단일 셸 앱 대상 멤버 푸시 자격. 고객 앱 자격은 이 env가 아니라
    // 워크스페이스별 WorkspacePushCredential로 해석한다. 미설정 시 해당 provider
    // 발송 no-op (코드 선배포 안전 — packages/push/lib/resolve-push-config.ts).
    // FCM 서비스 계정 JSON (base64 인코딩 권장).
    FCM_SERVICE_ACCOUNT_JSON: z.string().optional(),
    // APNs token-based auth (.p8) 자격.
    APNS_AUTH_KEY: z.string().optional(),
    APNS_KEY_ID: z.string().optional(),
    APNS_TEAM_ID: z.string().optional(),
    APNS_BUNDLE_ID: z.string().optional(),
    // "true"면 production APNs 엔드포인트, 아니면 sandbox.
    APNS_PRODUCTION: z.string().optional(),

    // === 플랫폼 SMS (Solapi) ===
    // ⚠️ 리옵트 **자체** 발송 전용 자격 — 고객 메시징(CRM/워크플로우)과 절대 섞지 말 것.
    // 고객 메시징(SMS/카카오, RFC-0011)은 워크스페이스별 Solapi 자격을 스튜디오 설정에서
    // 등록하며 이 env를 폴백으로 쓰지 않는다(packages/messaging/lib/resolve-messaging-config.ts).
    // 이 값들은 오직 sendPlatformSms()만 읽는다
    // (packages/messaging/lib/resolve-platform-messaging-config.ts).
    // 미설정 시 발송 no-op이 아니라 MessagingNotConfiguredError로 표면화.
    SOLAPI_API_KEY: z.string().optional(),
    SOLAPI_API_SECRET: z.string().optional(),
    // 사전 등록된 발신번호(숫자만, 예: 16442410).
    SOLAPI_SENDER_PHONE: z.string().optional(),
    // 🔴 온보딩 SMS 인증을 건너뛰는 내부 구성원 번호(쉼표 구분). 미설정 시
    // packages/auth/lib/phone-verification.ts의 기본 목록이 쓰인다. 우회를 전부
    // 끄려면 숫자가 없는 값(예: `none`)을 넣는다 — 빈 문자열은
    // emptyStringAsUndefined 때문에 "미설정"이 되어 기본 목록이 되살아난다.
    // 여기 실제 통화 가능한 번호를 넣지 말 것.
    PHONE_VERIFICATION_BYPASS_NUMBERS: z.string().optional(),

    // === 크론/웹훅 ===
    CRON_SECRET: z.string().optional(),
    /**
     * superadmin이 apps/web의 렌더 감사를 호출할 때 쓰는 공유 시크릿.
     * 미설정이면 감사 엔드포인트가 503으로 닫힌다(열린 채 두지 않는다).
     */
    BRANDFRONT_RENDER_AUDIT_TOKEN: z.string().optional(),
    NEXT_SERVER_ACTIONS_ENCRYPTION_KEY: z
      .string()
      .refine(isValidServerActionEncryptionKey, {
        message:
          "NEXT_SERVER_ACTIONS_ENCRYPTION_KEY must be base64 with a decoded length of 16, 24, or 32 bytes",
      })
      .optional(),
    // 양방향 자격증명 암호화 키 (AES-256-GCM) — server-core/secret-cipher.
    // 미설정 시 secret-cipher 사용 시점에만 에러(MCP 등 자격증명 기능만 비활성).
    // 생성: `openssl rand -base64 32`
    SECRET_ENCRYPTION_KEY: z
      .string()
      .refine(isValidSecretEncryptionKey, {
        message:
          "SECRET_ENCRYPTION_KEY must be base64 with a decoded length of 32 bytes (AES-256)",
      })
      .optional(),
    CSP_REPORT_ONLY: z.enum(["true", "false"]).optional(),
    CSP_REPORT_SAMPLE_RATE: z.coerce.number().min(0).max(1).optional(),

    // === 분석 ===
    META_PIXEL_ID: z.string().optional(),

    // === 파일 스토리지 (Vercel Blob) ===
    /** private 정본(canonical) 스토어 RW 토큰 — 모든 업로드/생성 자산이 여기서 태어난다 */
    BLOB_READ_WRITE_TOKEN: z.string().optional(),
    /**
     * public 배포 스토어 RW 토큰 (선택) — 게시된 공개 표면이 참조하는 이미지의 CDN 직행 사본.
     * 미설정 시 공개 서빙은 /api/blob 프록시(visibility=public)로 폴백한다(현행 동작).
     * 설정 시 게시 스냅샷이 public 스토어 직접 URL을 참조 → 함수 우회 + 저렴한 egress.
     */
    BLOB_PUBLIC_READ_WRITE_TOKEN: z.string().optional(),
    /**
     * public 배포 스토어 ID (예: `store_xxx`) — 정적 자산 CDN 베이스 URL 파생용(서버 전용,
     * OG 메타데이터 등). 워크스페이스 파일 승격은 이 값을 쓰지 않고 put() 반환 URL을 저장한다.
     */
    BLOB_PUBLIC_STORE_ID: z.string().optional(),
    /** 전역 템플릿 전용 public Blob store의 RW 토큰 */
    /**
     * Brandfront AI 편집 경로별 kill switch (계획 §15). 값이 "off"일 때만 닫힌다 —
     * 미설정·오타는 켜진 상태로 두어, 설정 실수가 조용한 기능 소실이 되지 않게 한다.
     * 하나를 끄는 것이 다른 경로를 끄지 않아야 하므로 경로마다 별도 변수를 둔다.
     */
    BRANDFRONT_AI_EVIDENCE_RESOLVER: z.string().optional(),
    BRANDFRONT_AI_SCOPED_EDIT: z.string().optional(),
    BRANDFRONT_AI_VISUAL_SECTION_EDIT: z.string().optional(),
    BRANDFRONT_AI_PAGE_GENERATION: z.string().optional(),
    /**
     * 쉼표로 구분한 Brandfront agent tool allowlist. 미설정이면 기존 전체 표면을 유지한다.
     * 사고 격리 중 도구를 하나씩 재개할 때 사용하며, 실제 ToolSet과 activeTools를 함께 줄인다.
     */
    BRANDFRONT_AI_TOOL_ALLOWLIST: z.string().optional(),
    BRANDFRONT_TEMPLATE_READ_WRITE_TOKEN: z.string().optional(),
    /** 모든 환경이 공유하는 전역 템플릿 전용 public Blob store ID */
    BRANDFRONT_TEMPLATE_STORE_ID: z.string().optional(),
    /** 향후 Blob webhook 수신 시 서명 검증용. 현재 server upload 흐름에서는 미사용. */
    BRANDFRONT_TEMPLATE_WEBHOOK_PUBLIC_KEY: z.string().optional(),

    // === 기타 ===
    PUPPETEER_EXECUTABLE_PATH: z.string().optional(),
    BUNDLE_ANALYZER: z.string().optional(),

    // === 보안 (Captcha) ===
    TURNSTILE_SECRET_KEY: z.string().optional(),
    DISABLE_CAPTCHA: z.string().optional(),

    // === 인가 (Authorization) ===
    /**
     * RBAC/ABAC 권한 입자 강제 모드 (packages/authorization/lib/feature-flag.ts).
     * - enforce : 권한 부족 시 차단 (프로덕션 목표 모드)
     * - audit   : 권한 부족해도 통과, 감사 로그만 기록 (미설정 시 기본값, fail-open)
     * - disabled: 권한 입자 검사 스킵
     * ⚠️ 멤버십/테넌트 격리는 이 값과 무관하게 게이트에서 항상 강제된다 —
     * 이 플래그는 "워크스페이스 내 RBAC 권한 입자"만 제어한다.
     * 미설정 시 audit로 동작하므로 prod는 enforce 명시를 권장한다.
     */
    AUTHORIZATION_MODE: z.enum(["enforce", "audit", "disabled"]).optional(),

    // === 데모 투어 점진 배포 ===
    // false면 즉시 전체 비활성. 비율은 사용자 ID의 안정적 버킷(0~99)에 적용한다.
    DEMO_TOURS_ENABLED: z.enum(["true", "false"]).optional(),
    DEMO_TOURS_ROLLOUT_PERCENT: z.coerce.number().min(0).max(100).optional(),

    // === 관측성 (Sentry) ===
    // 에러/트레이스 수집 DSN. 미설정 시 Sentry.init이 client 없이 반환되어
    // 아무 것도 수집하지 않는다 — 프로덕션 미설정을 부팅 시 경고로 드러내기 위해
    // 스키마에 명시 등록한다(sentry.server.config.ts가 소비). server DSN 우선,
    // 없으면 NEXT_PUBLIC_SENTRY_DSN로 폴백.
    SENTRY_DSN: z.string().optional(),
  },

  /**
   * Edge 런타임에서 process.env를 직접 destructure할 수 없으므로
   * 수동으로 매핑합니다.
   */
  runtimeEnv: {
    // Server
    NODE_ENV: process.env.NODE_ENV,
    DATABASE_URL: process.env.DATABASE_URL,
    AUTH_SECRET: process.env.AUTH_SECRET,
    BETTER_AUTH_SECRETS: process.env.BETTER_AUTH_SECRETS,
    GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
    APPLE_CLIENT_ID: process.env.APPLE_CLIENT_ID,
    APPLE_CLIENT_SECRET: process.env.APPLE_CLIENT_SECRET,
    APPLE_TEAM_ID: process.env.APPLE_TEAM_ID,
    APPLE_KEY_ID: process.env.APPLE_KEY_ID,
    APPLE_PRIVATE_KEY: process.env.APPLE_PRIVATE_KEY,
    APPLE_APP_BUNDLE_IDENTIFIER: process.env.APPLE_APP_BUNDLE_IDENTIFIER,
    GITHUB_MCP_CLIENT_ID: process.env.GITHUB_MCP_CLIENT_ID,
    GITHUB_MCP_CLIENT_SECRET: process.env.GITHUB_MCP_CLIENT_SECRET,
    GOOGLE_MCP_CLIENT_ID: process.env.GOOGLE_MCP_CLIENT_ID,
    GOOGLE_MCP_CLIENT_SECRET: process.env.GOOGLE_MCP_CLIENT_SECRET,
    DATABASE_PRISMA_DATABASE_URL: process.env.DATABASE_PRISMA_DATABASE_URL,
    DATABASE_POSTGRES_URL: process.env.DATABASE_POSTGRES_URL,
    PRISMA_DIRECT_URL: process.env.PRISMA_DIRECT_URL,
    DATABASE_DRIVER: process.env.DATABASE_DRIVER,
    AURORA_PGHOST: process.env.AURORA_PGHOST,
    AURORA_PGPORT: process.env.AURORA_PGPORT,
    AURORA_PGUSER: process.env.AURORA_PGUSER,
    AURORA_PGDATABASE: process.env.AURORA_PGDATABASE,
    AURORA_PGSSLMODE: process.env.AURORA_PGSSLMODE,
    AURORA_AWS_REGION: process.env.AURORA_AWS_REGION,
    AURORA_AWS_ROLE_ARN: process.env.AURORA_AWS_ROLE_ARN,
    AURORA_SSL_CA: process.env.AURORA_SSL_CA,
    AURORA_AUTH_TOKEN: process.env.AURORA_AUTH_TOKEN,
    REDIS_URL: process.env.REDIS_URL,
    REOPT_DATA_CLIENT_ID: process.env.REOPT_DATA_CLIENT_ID,
    REOPT_DATA_CLIENT_SECRET: process.env.REOPT_DATA_CLIENT_SECRET,
    REOPT_DATA_API_URL: process.env.REOPT_DATA_API_URL,
    REOPT_DATA_AI_CLIENT_ID: process.env.REOPT_DATA_AI_CLIENT_ID,
    REOPT_DATA_AI_CLIENT_SECRET: process.env.REOPT_DATA_AI_CLIENT_SECRET,
    REOPT_PLATFORM_KEY: process.env.REOPT_PLATFORM_KEY,
    PADDLE_API_KEY: process.env.PADDLE_API_KEY,
    PADDLE_NOTIFICATION_WEBHOOK_SECRET:
      process.env.PADDLE_NOTIFICATION_WEBHOOK_SECRET,
    TOSS_SECRET_KEY: process.env.TOSS_SECRET_KEY,
    TOSS_BILLING_SECRET_KEY: process.env.TOSS_BILLING_SECRET_KEY,
    TOSS_WEBHOOK_SECRET: process.env.TOSS_WEBHOOK_SECRET,
    EMAIL_MAILER: process.env.EMAIL_MAILER,
    EMAIL_SENDER: process.env.EMAIL_SENDER,
    EMAIL_FEEDBACK: process.env.EMAIL_FEEDBACK,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    RESEND_EMAIL_DOMAIN: process.env.RESEND_EMAIL_DOMAIN,
    EMAIL_RESEND_WEBHOOK_SECRET: process.env.EMAIL_RESEND_WEBHOOK_SECRET,
    EMAIL_SERVER: process.env.EMAIL_SERVER,
    EMAIL_SERVER_HOST: process.env.EMAIL_SERVER_HOST,
    EMAIL_SERVER_PORT: process.env.EMAIL_SERVER_PORT,
    EMAIL_SERVER_USER: process.env.EMAIL_SERVER_USER,
    EMAIL_SERVER_PASS: process.env.EMAIL_SERVER_PASS,
    EXPO_ACCESS_TOKEN: process.env.EXPO_ACCESS_TOKEN,
    FCM_SERVICE_ACCOUNT_JSON: process.env.FCM_SERVICE_ACCOUNT_JSON,
    APNS_AUTH_KEY: process.env.APNS_AUTH_KEY,
    APNS_KEY_ID: process.env.APNS_KEY_ID,
    APNS_TEAM_ID: process.env.APNS_TEAM_ID,
    APNS_BUNDLE_ID: process.env.APNS_BUNDLE_ID,
    APNS_PRODUCTION: process.env.APNS_PRODUCTION,
    SOLAPI_API_KEY: process.env.SOLAPI_API_KEY,
    SOLAPI_API_SECRET: process.env.SOLAPI_API_SECRET,
    SOLAPI_SENDER_PHONE: process.env.SOLAPI_SENDER_PHONE,
    PHONE_VERIFICATION_BYPASS_NUMBERS:
      process.env.PHONE_VERIFICATION_BYPASS_NUMBERS,
    CRON_SECRET: process.env.CRON_SECRET,
    BRANDFRONT_RENDER_AUDIT_TOKEN: process.env.BRANDFRONT_RENDER_AUDIT_TOKEN,
    NEXT_SERVER_ACTIONS_ENCRYPTION_KEY:
      process.env.NEXT_SERVER_ACTIONS_ENCRYPTION_KEY,
    SECRET_ENCRYPTION_KEY: process.env.SECRET_ENCRYPTION_KEY,
    CSP_REPORT_ONLY: process.env.CSP_REPORT_ONLY,
    CSP_REPORT_SAMPLE_RATE: process.env.CSP_REPORT_SAMPLE_RATE,
    META_PIXEL_ID: process.env.META_PIXEL_ID,
    BLOB_READ_WRITE_TOKEN: process.env.BLOB_READ_WRITE_TOKEN,
    BLOB_PUBLIC_READ_WRITE_TOKEN: process.env.BLOB_PUBLIC_READ_WRITE_TOKEN,
    BLOB_PUBLIC_STORE_ID: process.env.BLOB_PUBLIC_STORE_ID,
    BRANDFRONT_AI_EVIDENCE_RESOLVER:
      process.env.BRANDFRONT_AI_EVIDENCE_RESOLVER,
    BRANDFRONT_AI_SCOPED_EDIT: process.env.BRANDFRONT_AI_SCOPED_EDIT,
    BRANDFRONT_AI_VISUAL_SECTION_EDIT:
      process.env.BRANDFRONT_AI_VISUAL_SECTION_EDIT,
    BRANDFRONT_AI_PAGE_GENERATION: process.env.BRANDFRONT_AI_PAGE_GENERATION,
    BRANDFRONT_AI_TOOL_ALLOWLIST: process.env.BRANDFRONT_AI_TOOL_ALLOWLIST,
    BRANDFRONT_TEMPLATE_READ_WRITE_TOKEN:
      process.env.BRANDFRONT_TEMPLATE_READ_WRITE_TOKEN,
    BRANDFRONT_TEMPLATE_STORE_ID: process.env.BRANDFRONT_TEMPLATE_STORE_ID,
    BRANDFRONT_TEMPLATE_WEBHOOK_PUBLIC_KEY:
      process.env.BRANDFRONT_TEMPLATE_WEBHOOK_PUBLIC_KEY,
    PUPPETEER_EXECUTABLE_PATH: process.env.PUPPETEER_EXECUTABLE_PATH,
    BUNDLE_ANALYZER: process.env.BUNDLE_ANALYZER,
    TURNSTILE_SECRET_KEY: process.env.TURNSTILE_SECRET_KEY,
    DISABLE_CAPTCHA: process.env.DISABLE_CAPTCHA,
    AUTHORIZATION_MODE: process.env.AUTHORIZATION_MODE,
    DEMO_TOURS_ENABLED: process.env.DEMO_TOURS_ENABLED,
    DEMO_TOURS_ROLLOUT_PERCENT: process.env.DEMO_TOURS_ROLLOUT_PERCENT,
    SENTRY_DSN: process.env.SENTRY_DSN,
  },

  /**
   * 빈 문자열을 undefined로 처리
   */
  emptyStringAsUndefined: true,

  onValidationError: formatValidationError,
  onInvalidAccess: handleInvalidAccess,
  skipValidation: process.env.SKIP_ENV_VALIDATION === "true",
});

// 교차 규칙: DB 접속 수단은 둘 중 하나여야 한다 — URL(로컬) 또는 Aurora 드라이버.
// t3-env의 필드 단위 스키마로는 적을 수 없어 여기서 같은 포맷으로 던진다.
//
// 🔴 **반드시 서버에서만 평가한다.** 이 모듈은 `clientEnv`도 함께 내보내므로 클라이언트
// 번들에 전이 import로 딸려 들어간다. 그 상태에서 최상위가 `env.DATABASE_URL`(서버 전용)을
// 읽으면 t3-env의 `onInvalidAccess`가 **모듈 평가 시점에** 던지고, 그 모듈을 물고 있는
// 화면이 통째로 죽는다 — 브라우저 콘솔에 "서버 전용 환경변수를 클라이언트에서 접근할 수
// 없습니다"만 남아 원인이 DB 설정처럼 보인다(실제 원인은 이 줄의 평가 위치다).
// 서버 전용 값을 검사하는 규칙이므로 브라우저에서 돌 이유도 없다.
if (
  typeof window === "undefined" &&
  process.env.SKIP_ENV_VALIDATION !== "true" &&
  !env.DATABASE_URL &&
  env.DATABASE_DRIVER !== "aurora"
) {
  formatValidationError([
    {
      path: ["DATABASE_URL"],
      message:
        "DATABASE_URL is required unless DATABASE_DRIVER=aurora (AURORA_* 로 접속)",
    },
  ]);
}

// === 타입 내보내기 ===
export type ServerEnv = typeof env;

/**
 * 클라이언트에서 안전하게 사용할 수 있는 환경변수
 *
 * 'use client' 컴포넌트에서는 `env` 대신 `clientEnv`를 사용하세요.
 *
 * @example
 * ```tsx
 * 'use client';
 * import { clientEnv } from '#/env';
 *
 * export function MyComponent() {
 *   return <a href={clientEnv.NEXT_PUBLIC_SITE_URL}>사이트</a>;
 * }
 * ```
 */
export const clientEnv = createEnv({
  /**
   * 클라이언트 측 환경변수 스키마
   * NEXT_PUBLIC_ 접두사가 필요합니다.
   */
  client: {
    NEXT_PUBLIC_ENVIRONMENT: z
      .enum(["development", "staging", "production"])
      .default("development"),
    NEXT_PUBLIC_SITE_URL: z.string().url().optional(),
    NEXT_PUBLIC_APP_URL: z.string().url().optional(),
    NEXT_PUBLIC_ID_URL: z.string().url().optional(),
    NEXT_PUBLIC_PADDLE_ENV: z.enum(["sandbox", "production"]).optional(),
    NEXT_PUBLIC_PADDLE_CLIENT_TOKEN: z.string().optional(),
    NEXT_PUBLIC_TOSS_CLIENT_KEY: z.string().optional(),
    NEXT_PUBLIC_TOSS_BILLING_CLIENT_KEY: z.string().optional(),
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: z.string().optional(),
    // Google Analytics 4 measurement id (G-XXXX). 미설정 시 GA 비활성.
    // studio 웹앱 분석 — 데이터 분리를 위해 앱/웹 스트림을 나눈다:
    //   NEXT_PUBLIC_GA_ID     = 일반 브라우저(웹 스트림)
    //   NEXT_PUBLIC_GA_ID_APP = 모바일 앱 웹뷰(앱 전용 스트림)
    NEXT_PUBLIC_GA_ID: z.string().optional(),
    NEXT_PUBLIC_GA_ID_APP: z.string().optional(),
    // Sentry 클라이언트 DSN(브라우저 SDK). 서버 SENTRY_DSN의 폴백으로도 쓰인다.
    NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
  },

  /**
   * Edge 런타임에서 process.env를 직접 destructure할 수 없으므로
   * 수동으로 매핑합니다.
   */
  runtimeEnv: {
    NEXT_PUBLIC_ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT as
      | "development"
      | "staging"
      | "production",
    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_ID_URL: process.env.NEXT_PUBLIC_ID_URL,
    NEXT_PUBLIC_PADDLE_ENV: process.env.NEXT_PUBLIC_PADDLE_ENV as
      | "sandbox"
      | "production"
      | undefined,
    NEXT_PUBLIC_PADDLE_CLIENT_TOKEN:
      process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN,
    NEXT_PUBLIC_TOSS_CLIENT_KEY: process.env.NEXT_PUBLIC_TOSS_CLIENT_KEY,
    NEXT_PUBLIC_TOSS_BILLING_CLIENT_KEY:
      process.env.NEXT_PUBLIC_TOSS_BILLING_CLIENT_KEY,
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
    NEXT_PUBLIC_GA_ID: process.env.NEXT_PUBLIC_GA_ID,
    NEXT_PUBLIC_GA_ID_APP: process.env.NEXT_PUBLIC_GA_ID_APP,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
  },

  /**
   * 빈 문자열을 undefined로 처리
   */
  emptyStringAsUndefined: true,

  onValidationError: formatValidationError,
  skipValidation: process.env.SKIP_ENV_VALIDATION === "true",
});

/** 프로덕션 환경 여부 */
export const IS_PRODUCTION =
  process.env.NEXT_PUBLIC_ENVIRONMENT === "production";

/** Vercel preview deploy + NEXT_PUBLIC_ENVIRONMENT=staging (reopt.io 도메인) */
export const IS_STAGING = process.env.NEXT_PUBLIC_ENVIRONMENT === "staging";

/** 프로덕션이 아닌 모든 환경 (development + staging) */
export const IS_NON_PROD = !IS_PRODUCTION;

export type ClientEnv = typeof clientEnv;
